/**
 * Feature detection for glassmorphism effects
 * Phase 1: Foundation & Architecture
 */

export interface GlassmorphismSupport {
  backdropFilter: boolean
  webkitBackdropFilter: boolean
  cssCustomProperties: boolean
  supportsApi: boolean
}

/**
 * Check if the browser supports glassmorphism effects
 */
export function supportsGlassmorphism(): GlassmorphismSupport {
  const hasCSS = typeof CSS !== 'undefined' && typeof CSS.supports === 'function'

  return {
    backdropFilter: hasCSS && CSS.supports('backdrop-filter', 'blur(1px)'),
    webkitBackdropFilter: hasCSS && CSS.supports('-webkit-backdrop-filter', 'blur(1px)'),
    cssCustomProperties: hasCSS && CSS.supports('color', 'var(--test)'),
    supportsApi: hasCSS,
  }
}

/**
 * Check if any form of backdrop-filter is supported
 */
export function hasAnyBackdropFilter(): boolean {
  const support = supportsGlassmorphism()
  return support.backdropFilter || support.webkitBackdropFilter
}

/**
 * Get the appropriate backdrop-filter property name
 */
export function getBackdropFilterProperty(): string | null {
  const support = supportsGlassmorphism()
  
  if (support.backdropFilter) {
    return 'backdrop-filter'
  }
  
  if (support.webkitBackdropFilter) {
    return '-webkit-backdrop-filter'
  }
  
  return null
}

/**
 * Check if we're on a mobile device
 * Useful for performance optimizations
 */
export function isMobileDevice(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  )
}

/**
 * Check if we're on a low-power device
 * Useful for reducing animation complexity
 */
export function isLowPowerDevice(): boolean {
  // Check for touch device (often mobile)
  const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
  
  // Check for reduced motion preference (battery saver mode often enables this)
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  
  // Check for small screen (mobile indicator)
  const isSmallScreen = window.innerWidth < 768
  
  return (isTouch && isSmallScreen) || prefersReducedMotion
}

/**
 * Get recommended quality tier based on device capabilities
 */
export type QualityTier = 'high' | 'medium' | 'low'

export function getQualityTier(): QualityTier {
  // Check for explicitly low-end conditions
  if (isLowPowerDevice()) {
    return 'low'
  }
  
  // Check for backdrop-filter support
  if (!hasAnyBackdropFilter()) {
    return 'low'
  }
  
  // Check for large screen (likely desktop)
  if (window.innerWidth >= 1440 && !isMobileDevice()) {
    return 'high'
  }
  
  return 'medium'
}
